import type {
  StarterCoordinationResponse,
  StarterRowResponse,
} from "../services/starterCoordination";
import type {
  ProspectiveCurrentSource,
  StarterCoordinationWarningCode,
} from "../services/starterCoordinationContract";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import {
  STATUS_LABELS as UPS_STATUS_LABELS,
  STATUS_TONE as UPS_STATUS_TONE,
} from "./UPSResultSummary";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";

// The same words and the same colour as the Transformer, DG, UPS and PV summaries
// for a calculated, a warned and a review-required study: an unestablished input is
// a review the engineer owes, not a design failure. A failed check is a failure. The
// maps are the UPS summary's, typed here against the starter coordination status so
// a value only this module gains cannot pass without words and a colour; the
// NO_SOLUTION words are this module's own, because "no standard rating fits" is not
// what a failed coordination check means.
const STATUS_LABELS: Record<StarterCoordinationResponse["status"], string> = {
  ...UPS_STATUS_LABELS,
  NO_SOLUTION: "Coordination not met",
};

const STATUS_TONE: Record<StarterCoordinationResponse["status"], "pass" | "fail" | "warning"> =
  UPS_STATUS_TONE;

const SOURCE_LABELS: Record<ProspectiveCurrentSource, string> = {
  LINKED_FAULT_RUN: "Linked fault run",
  TYPED: "Typed value",
};

// A row whose certificate is incomplete: the evidence the engineer still owes (A20 (e)).
const NOT_ESTABLISHED_ROW_CODES = [
  "IQ_NOT_ESTABLISHED",
  "TEST_VOLTAGE_NOT_ESTABLISHED",
  "CERTIFICATE_REFERENCE_NOT_ESTABLISHED",
] as const satisfies readonly StarterCoordinationWarningCode[];

const CURRENT_CHECKS_NOT_EVALUATED: StarterCoordinationWarningCode =
  "STARTER_CURRENT_CHECKS_NOT_EVALUATED";

export type RowKind = "coordinated" | "partially-checked" | "not-met" | "review";

/**
 * How one starter fared (IR-001 F3). A starter whose current checks were not made (a
 * starting method other than DOL) is partially checked and is never presented as
 * coordinated; a failed check is not met; missing or low-voltage evidence is a review.
 */
export function rowKind(row: StarterRowResponse): RowKind {
  if (row.status === "NO_SOLUTION") {
    return "not-met";
  }
  if (row.status === "REVIEW_REQUIRED") {
    return "review";
  }
  return currentChecksNotEvaluated(row) ? "partially-checked" : "coordinated";
}

export function currentChecksNotEvaluated(row: StarterRowResponse): boolean {
  return row.warnings.some((warning) => warning.code === CURRENT_CHECKS_NOT_EVALUATED);
}

/** "Coordination not met for 1 starter" / "... for 3 starters"; the other statuses' own words. */
export function statusHeading(result: StarterCoordinationResponse): string {
  if (result.status !== "NO_SOLUTION") {
    return STATUS_LABELS[result.status];
  }
  const notMet = result.rows.filter((row) => row.status === "NO_SOLUTION").length;
  return `${STATUS_LABELS.NO_SOLUTION} for ${notMet} ${notMet === 1 ? "starter" : "starters"}`;
}

/**
 * The row with the smallest Iq margin, or null when no row has a certified Iq. The
 * margins are the engine's exact strings; they are only compared here, never recomputed,
 * and a tie keeps the first row.
 */
export function lowestIqMargin(rows: readonly StarterRowResponse[]): StarterRowResponse | null {
  let lowest: StarterRowResponse | null = null;
  for (const row of rows) {
    if (row.iq_margin_ka === null) {
      continue;
    }
    if (lowest === null || Number(row.iq_margin_ka) < Number(lowest.iq_margin_ka)) {
      lowest = row;
    }
  }
  return lowest;
}

function hasNotEstablishedCode(row: StarterRowResponse): boolean {
  return row.warnings.some((warning) =>
    (NOT_ESTABLISHED_ROW_CODES as readonly string[]).includes(warning.code),
  );
}

type StarterCoordinationResultSummaryProps = {
  result: StarterCoordinationResponse;
};

// The decisive values first (Master Prompt v2.1 section 19): how many starters were
// checked and how they fared, the fault level they were held against and where it
// came from, and the tightest Iq margin. No figure is calculated here: the margins
// are the engine's, and the tiles only count row statuses and pick an existing value.
// Display rule A12: four significant figures on screen, the exact engine decimal in
// the tooltip (title attribute).
export function StarterCoordinationResultSummary({
  result,
}: StarterCoordinationResultSummaryProps) {
  const tone = STATUS_TONE[result.status];
  const rows = result.rows;

  const kinds = rows.map(rowKind);
  const coordinated = kinds.filter((kind) => kind === "coordinated").length;
  const partiallyChecked = kinds.filter((kind) => kind === "partially-checked").length;
  const notMet = kinds.filter((kind) => kind === "not-met").length;
  const review = kinds.filter((kind) => kind === "review").length;
  const notEstablishedCount = rows.filter(hasNotEstablishedCode).length;
  // Every starter whose current checks were not made, whatever else its row says (IR-001 F2).
  const notEvaluated = rows.filter(currentChecksNotEvaluated);
  const warningCount =
    result.warnings.length + rows.reduce((count, row) => count + row.warnings.length, 0);

  const faultLevel = formatQuantity(result.prospective_current_ka, "kA");
  const lowest = lowestIqMargin(rows);
  const margin = formatQuantity(lowest?.iq_margin_ka ?? null, "kA");

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STATUS_TONE)} size={20} />
          {statusHeading(result)}
        </p>
        <p>
          Study <strong>{result.code}</strong> · {result.name} ·{" "}
          {result.jurisdiction_profile} profile
        </p>
      </header>
      <dl data-summary-strip>
        <div>
          <dt>Starters</dt>
          {/* Counts of row statuses, not engineering quantities. */}
          <dd data-summary-field="starters" data-summary-value="text">
            {rows.length} checked · {coordinated} coordinated · {partiallyChecked} partially
            checked · {notMet} not met · {review} review
          </dd>
        </div>
        <div>
          <dt>Fault level</dt>
          <dd data-summary-field="fault-level" title={faultLevel.exact ?? undefined}>
            {faultLevel.display} at {result.fault_bus_code}
          </dd>
        </div>
        <div>
          <dt>Fault level source</dt>
          <dd data-summary-field="fault-level-source" data-summary-value="text" data-current-source={result.current_source}>
            {SOURCE_LABELS[result.current_source]}
          </dd>
        </div>
        <div>
          <dt>Lowest Iq margin</dt>
          {/* Iq - Ik'' of the tightest starter, as the engine reported it; negative when
              that starter's check failed. Empty when no row has a certified Iq. */}
          <dd data-summary-field="lowest-iq-margin" title={margin.exact ?? undefined}>
            {lowest === null ? EMPTY_QUANTITY : `${margin.display} (${lowest.motor_code})`}
          </dd>
        </div>
        {notEvaluated.length > 0 ? (
          <div>
            <dt>Current checks not evaluated</dt>
            <dd data-summary-field="current-checks-not-evaluated" data-summary-value="text">
              {notEvaluated.length} {notEvaluated.length === 1 ? "starter" : "starters"} (
              {notEvaluated.map((row) => row.motor_code).join(", ")})
            </dd>
          </div>
        ) : null}
        {notEstablishedCount > 0 ? (
          <div>
            <dt>Inputs not established</dt>
            <dd data-summary-field="not-established">{notEstablishedCount}</dd>
          </div>
        ) : null}
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={warningCount}>{warningCount}</dd>
        </div>
      </dl>
    </section>
  );
}

export { NOT_ESTABLISHED_ROW_CODES, SOURCE_LABELS, STATUS_LABELS, STATUS_TONE };
